import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import supabase from '../lib/supabaseClient';

export default function MoverDetails() {
  const { slug } = useParams(); // This is the mover ID
  const navigate = useNavigate();
  const [mover, setMover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviews, setShowReviews] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });

  useEffect(() => {
    const fetchMoverDetails = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch mover data from movers_data table
        const { data: moverData, error } = await supabase
          .from('movers_data')
          .select('*')
          .eq('id', slug)
          .single();

        if (error) {
        } else {
          setMover(moverData);
        }

        // Fetch reviews for this mover
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('mover_reviews')
          .select('*')
          .eq('mover_id', slug)
          .eq('is_approved', true)
          .order('created_at', { ascending: false });

        if (reviewsError) {
        } else if (reviewsData && reviewsData.length > 0) {
          setReviews(reviewsData);

          // Calculate review statistics
          const totalReviews = reviewsData.length;
          const sumRatings = reviewsData.reduce((sum, review) => sum + review.rating, 0);
          const averageRating = (sumRatings / totalReviews).toFixed(1);

          // Calculate distribution
          const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
          reviewsData.forEach(review => {
            distribution[review.rating]++;
          });

          setReviewStats({
            averageRating,
            totalReviews,
            distribution
          });
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchMoverDetails();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="text-gray-600 mt-4">Loading mover details...</p>
        </div>
      </div>
    );
  }

  if (!mover) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="mb-4 text-sm text-blue-600 hover:underline">← Back</button>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
          <div className="text-4xl mb-4">🚚</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Mover Not Found</h2>
          <p className="text-gray-600">The mover you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const initials = mover.company_name
    ? mover.company_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Helmet>
        <title>{mover.company_name ? `${mover.company_name} – MyMoveAdvisor` : 'Mover Details – MyMoveAdvisor'}</title>
        <meta name="description" content={mover.bio || `View details, reviews, and contact info for ${mover.company_name || 'this mover'} on MyMoveAdvisor.`} />
      </Helmet>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-blue-600 hover:underline">← Back</button>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          {mover.company_logo ? (
            <img 
              src={mover.company_logo} 
              alt={mover.company_name}
              className="w-16 h-16 rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-16 h-16 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-semibold text-lg"
            style={{ display: mover.company_logo ? 'none' : 'flex' }}
          >
            {initials}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{mover.company_name}</h1>
              {mover.is_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                  ✓ Verified
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
              <span className="text-yellow-500">★</span>
              <span className="font-medium">{reviewStats.averageRating > 0 ? reviewStats.averageRating : 'N/A'}</span>
              {reviewStats.totalReviews > 0 && (
                <>
                  <span className="text-gray-400">•</span>
                  <span>{reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
            <div className="space-y-2 text-sm">
              {mover.phone && (
                <div className="flex items-center gap-2">
                  <span>📞</span>
                  <span className="text-gray-900">{mover.phone}</span>
                </div>
              )}
              {mover.email && (
                <div className="flex items-center gap-2">
                  <span>✉️</span>
                  <span className="text-gray-900">{mover.email}</span>
                </div>
              )}
              {mover.website && (
                <div className="flex items-center gap-2">
                  <span>🌐</span>
                  <a href={mover.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {mover.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Location</h3>
            <div className="space-y-2 text-sm">
              {mover.city && mover.state && (
                <div className="flex items-center gap-2">
                  <span>📍</span>
                  <span className="text-gray-900">{mover.city}, {mover.state}</span>
                </div>
              )}
              {mover.business_address && (
                <div className="flex items-start gap-2">
                  <span>🏢</span>
                  <span className="text-gray-900">{mover.business_address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {mover.years_in_business && (
              <div>
                <div className="text-gray-600">Years in Business</div>
                <div className="font-semibold text-gray-900">{mover.years_in_business} years</div>
              </div>
            )}
            {mover.mc_number && (
              <div>
                <div className="text-gray-600">MC Number</div>
                <div className="font-semibold text-gray-900">{mover.mc_number}</div>
              </div>
            )}
            {mover.usdot_number && (
              <div>
                <div className="text-gray-600">USDOT Number</div>
                <div className="font-semibold text-gray-900">{mover.usdot_number}</div>
              </div>
            )}
          </div>
        </div>

        {/* Services & Coverage */}
        {mover.service_coverage && (
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Service Coverage</h3>
            <div className="text-sm text-gray-900">{mover.service_coverage}</div>
          </div>
        )}

        {/* Additional Information */}
        {mover.company_description && (
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">About</h3>
            <div className="text-sm text-gray-900">{mover.company_description}</div>
          </div>
        )}

        {/* Ratings & Reviews Section */}
        <div className="mt-8 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-gray-900">Ratings & Reviews</div>
            <button 
              onClick={() => setShowReviews(s => !s)} 
              className="text-sm text-blue-600 hover:underline"
            >
              {showReviews ? 'Hide' : 'View all'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(stars => {
                const count = reviewStats.distribution[stars] || 0;
                const percentage = reviewStats.totalReviews > 0 
                  ? ((count / reviewStats.totalReviews) * 100).toFixed(0)
                  : 0;
                return (
                  <div key={stars} className="flex items-center gap-3">
                    <span className="w-10 text-sm text-gray-700">{stars}★</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded">
                      <div className="h-2 rounded bg-yellow-400" style={{ width: `${percentage}%` }} />
                    </div>
                    <span className="w-12 text-right text-xs text-gray-500">{percentage}%</span>
                  </div>
                );
              })}
            </div>
            <div className="text-sm text-gray-600">
              Average rating <span className="font-semibold text-gray-900">{reviewStats.averageRating > 0 ? reviewStats.averageRating : 'N/A'}</span> based on {reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'}.
            </div>
          </div>

          {showReviews && (
            <div className="mt-6 space-y-4">
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <div key={review.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
                        {review.reviewer_name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900">{review.reviewer_name || 'Customer'}</div>
                            <div className="flex items-center gap-1 mt-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={star} className={`text-sm ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                                  ★
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-700">{review.review_text}</p>
                        
                        {review.mover_reply && (
                          <div className="mt-3 pl-4 border-l-2 border-blue-200 bg-blue-50/50 p-3 rounded">
                            <div className="text-xs font-semibold text-gray-700 mb-1">Response from {mover.company_name}</div>
                            <p className="text-sm text-gray-700">{review.mover_reply}</p>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(review.mover_reply_date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No reviews available yet
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
